import React, { useMemo, useState } from 'react';
import { Box, Button, Flex, SimpleGrid, Spinner, Text } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowClockwise } from 'phosphor-react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { centralApi, IGrupoResumo, ITelaCatalogo } from '../../hooks/useCentralPermissoes';
import { montarTelasDoSistema, useTelas } from '../../hooks/useTelas';
import { moduloDaTela, nomeDaTela, rotuloDaAcao } from './modulos';
import {
  AZUL,
  AZUL_ESCURO,
  Busca,
  Cartao,
  Etiqueta,
  Eyebrow,
  MONO,
  Pilula,
  TEXTO_SUAVE,
} from './ui';

export interface IDiferencas {
  entram: { codigo: string; nome: string; reativa: boolean }[];
  saem: ITelaCatalogo[];
  acoesNovas: { codigo: string; nome: string; acoes: string[] }[];
  total: number;
}

/** O que o código do front tem e o catálogo do banco ainda não (e vice-versa). */
export function compararComOSistema(catalogo: ITelaCatalogo[]): IDiferencas {
  const sistema = montarTelasDoSistema();
  const porCodigo = new Map(catalogo.map(t => [t.codigo, t]));
  const codigosSistema = new Set(sistema.map(t => t.codigo));
  const entram: IDiferencas['entram'] = [];
  const acoesNovas: IDiferencas['acoesNovas'] = [];
  sistema.forEach(t => {
    const noBanco = porCodigo.get(t.codigo);
    if (!noBanco || !noBanco.ativo) {
      entram.push({ codigo: t.codigo, nome: t.nome, reativa: !!noBanco });
      return;
    }
    const existentes = new Set(noBanco.acoes.map(a => a.acao));
    const novas = t.acoes.filter(a => !existentes.has(a));
    if (novas.length)
      acoesNovas.push({ codigo: t.codigo, nome: nomeDaTela(noBanco), acoes: novas });
  });
  const saem = catalogo.filter(t => t.ativo && !codigosSistema.has(t.codigo));
  return { entram, saem, acoesNovas, total: entram.length + saem.length + acoesNovas.length };
}

export function useSincronizarTelas() {
  const queryClient = useQueryClient();
  const { syncTelas } = useTelas();
  return useMutation({
    mutationFn: syncTelas,
    onSuccess: async res => {
      await queryClient.invalidateQueries({ queryKey: ['central'] });
      const r = res.data;
      toast.success('Catálogo de telas atualizado', {
        description: `${r.criadas} novas, ${r.desativadas} desativadas, ${r.atualizadas} conferidas`,
      });
    },
    onError: () => toast.error('Não foi possível sincronizar as telas'),
  });
}

type Filtro = 'todas' | 'ativas' | 'inativas';

/** Conteúdo do Catálogo de telas (aba da Central). */
export function CatalogoConteudo() {
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('ativas');
  const { data: catalogo = [], isLoading } = useQuery({
    queryKey: ['central', 'catalogo'],
    queryFn: centralApi.catalogo,
  });
  const { data: grupos = [] } = useQuery({
    queryKey: ['central', 'grupos-resumo'],
    queryFn: centralApi.gruposResumo,
  });
  const sincronizar = useSincronizarTelas();
  const dif = useMemo(() => compararComOSistema(catalogo), [catalogo]);
  const nomeGrupo = useMemo(
    () => new Map(grupos.map((g: IGrupoResumo) => [g.id, g.nome])),
    [grupos],
  );

  const q = busca.trim().toLowerCase();
  const linhas = catalogo
    .filter(t => filtro === 'todas' || (filtro === 'ativas' ? t.ativo : !t.ativo))
    .filter(t => !q || `${nomeDaTela(t)} ${t.codigo} ${t.path}`.toLowerCase().includes(q));

  return (
    <Flex direction="column" gap="16px">
      <Cartao p="20px 22px" display="flex" flexDirection="column" gap="16px">
        <Flex justify="space-between" align="flex-start" gap="12px" wrap="wrap">
          <Box>
            <Text fontSize="16px" fontWeight={650}>
              {dif.total ? 'O código tem novidades' : 'Catálogo em dia com o código'}
            </Text>
            <Text fontSize="13px" color={TEXTO_SUAVE}>
              Comparação entre as telas do sistema publicado e este catálogo. Nada muda até você
              confirmar.
            </Text>
          </Box>
          <Button
            leftIcon={<ArrowClockwise size={16} />}
            borderRadius="999px"
            color="white"
            bg={`linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})`}
            _hover={{ bg: AZUL_ESCURO }}
            isDisabled={!dif.total}
            isLoading={sincronizar.isPending}
            onClick={() => sincronizar.mutate()}
          >
            {dif.total
              ? `Sincronizar ${dif.entram.length + dif.acoesNovas.length + dif.saem.length} mudanças`
              : 'Nada a sincronizar'}
          </Button>
        </Flex>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing="12px">
          <Box p="14px" borderRadius="18px" bg="rgba(220,252,231,.65)">
            <Flex justify="space-between" align="baseline">
              <Eyebrow color="#166534">ENTRAM</Eyebrow>
              <Text fontFamily={MONO} fontSize="18px" fontWeight={600} color="#166534">
                {dif.entram.length}
              </Text>
            </Flex>
            {dif.entram.map(t => (
              <Box key={t.codigo} mt="6px">
                <Text fontSize="13px" fontWeight={600}>
                  {t.nome} {t.reativa && <Etiqueta tom="info">volta</Etiqueta>}
                </Text>
                <Text fontFamily={MONO} fontSize="11px" color="#475569">
                  {t.codigo}
                </Text>
              </Box>
            ))}
            {dif.acoesNovas.map(t => (
              <Box key={t.codigo} mt="6px">
                <Text fontSize="13px" fontWeight={600}>
                  {t.nome}
                </Text>
                <Text fontSize="11.5px" color="#475569">
                  ações novas: {t.acoes.map(rotuloDaAcao).join(', ')}
                </Text>
              </Box>
            ))}
          </Box>
          <Box p="14px" borderRadius="18px" bg="rgba(248,250,252,.9)">
            <Flex justify="space-between" align="baseline">
              <Eyebrow>SAEM DO CATÁLOGO</Eyebrow>
              <Text fontFamily={MONO} fontSize="18px" fontWeight={600} color="#334155">
                {dif.saem.length}
              </Text>
            </Flex>
            {dif.saem.map(t => (
              <Text key={t.id} mt="6px" fontSize="13px" fontWeight={600}>
                {nomeDaTela(t)}
              </Text>
            ))}
            <Text mt="8px" fontSize="12.5px" color="#475569">
              Tela que sai fica inativa e as permissões dela são guardadas, não apagadas.
            </Text>
          </Box>
          <Box
            p="14px"
            borderRadius="18px"
            bg="rgba(254,252,232,.9)"
            boxShadow="inset 0 0 0 1px #FDE68A"
          >
            <Eyebrow color="#92400E">AÇÕES PRESERVADAS</Eyebrow>
            <Text mt="6px" fontSize="13px" color="#92400E">
              Ação definida só no backend (como Emitir e Cancelar da NFS-e) não é apagada pela
              sincronização.
            </Text>
          </Box>
        </SimpleGrid>
      </Cartao>

      <Cartao p={0} overflow="hidden">
        <Flex
          align="center"
          gap="10px"
          wrap="wrap"
          p="16px 20px"
          borderBottom="1px solid rgba(15,23,42,.07)"
        >
          <Box flex="1 1 240px" maxW="340px">
            <Busca
              valor={busca}
              onChange={setBusca}
              placeholder="Buscar por nome, código ou caminho"
            />
          </Box>
          <Pilula
            ativa={filtro === 'ativas'}
            onClick={() => setFiltro('ativas')}
            contagem={catalogo.filter(t => t.ativo).length}
          >
            Ativas
          </Pilula>
          <Pilula
            ativa={filtro === 'inativas'}
            onClick={() => setFiltro('inativas')}
            contagem={catalogo.filter(t => !t.ativo).length}
          >
            Inativas
          </Pilula>
          <Pilula
            ativa={filtro === 'todas'}
            onClick={() => setFiltro('todas')}
            contagem={catalogo.length}
          >
            Todas
          </Pilula>
        </Flex>
        {isLoading ? (
          <Flex justify="center" py={12}>
            <Spinner color={AZUL} />
          </Flex>
        ) : (
          <Box overflowX="auto">
            <Box as="table" w="100%" minW="980px" style={{ borderCollapse: 'collapse' }}>
              <Box as="thead">
                <Box
                  as="tr"
                  fontFamily={MONO}
                  fontSize="10px"
                  letterSpacing="1px"
                  color={TEXTO_SUAVE}
                >
                  {['TELA', 'CÓDIGO', 'MÓDULO', 'AÇÕES', 'GRUPOS', 'SITUAÇÃO'].map(h => (
                    <Box as="th" key={h} textAlign="left" p="10px 16px">
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box as="tbody">
                {linhas.map(t => (
                  <Box as="tr" key={t.id} borderTop="1px solid rgba(15,23,42,.05)">
                    <Box as="td" p="10px 16px">
                      <Text fontSize="13.5px" fontWeight={700}>
                        {nomeDaTela(t)}
                      </Text>
                      <Text fontFamily={MONO} fontSize="11px" color={TEXTO_SUAVE}>
                        {t.path}
                      </Text>
                    </Box>
                    <Box as="td" p="10px 16px" fontFamily={MONO} fontSize="12px">
                      {t.codigo}
                    </Box>
                    <Box as="td" p="10px 16px" fontSize="13px">
                      {moduloDaTela(t).nome}
                    </Box>
                    <Box as="td" p="10px 16px" fontSize="12.5px" maxW="260px">
                      {t.acoes.map(a => rotuloDaAcao(a.acao)).join(', ') || '—'}
                    </Box>
                    <Box
                      as="td"
                      p="10px 16px"
                      fontSize="12.5px"
                      maxW="220px"
                      title={t.grupos.map(g => nomeGrupo.get(g) ?? g).join(', ')}
                    >
                      {t.grupos.length ? t.grupos.map(g => nomeGrupo.get(g) ?? g).join(', ') : '—'}
                    </Box>
                    <Box as="td" p="10px 16px">
                      <Etiqueta tom={t.ativo ? 'sucesso' : 'neutro'}>
                        {t.ativo ? 'Ativa' : 'Inativa'}
                      </Etiqueta>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Cartao>
    </Flex>
  );
}

/** Endereço antigo do catálogo: agora é a aba Catálogo da Central. */
export function CatalogoTelasPage() {
  return <Navigate to="/configuracoes/permissoes?modo=catalogo" replace />;
}
